import React from 'react';
import { Avatar, Box, Button, Chip, CircularProgress, Tooltip, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_LUXE_SECONDARY,
    BRAND_SURFACE_CONTAINER_LOW,
    BRAND_SURFACE_CONTAINER_HIGHEST,
    BRAND_LUXE_ERROR,
} from '../brandDashboardTokens';

const CONTENT_STATUS_COLORS: Record<string, string> = {
    'Content Pending': BRAND_LUXE_SECONDARY,
    Delivered: BRAND_LUXE_PRIMARY,
    'In Review': BRAND_LUXE_PRIMARY,
    Draft: 'rgba(255,255,255,0.4)',
    'Revision Requested': BRAND_LUXE_ERROR,
    Approved: BRAND_LUXE_SECONDARY,
};

export interface IParticipantProfileHeaderProps {
    name: string;
    handle: string;
    avatar?: string;
    profileUrl?: string;
    isPending: boolean;
    contentStatus?: string | null;
    tags: string[];
    followersLabel: string;
    engagementLabel: string;
    platform?: string;
    onMarkComplete?: () => void;
    markCompleteLoading?: boolean;
    canMarkComplete?: boolean;
    /** Deliverable types still missing live URLs — shown in tooltip when button is disabled */
    blockingDeliverables?: string[];
}

export function ParticipantProfileHeader({
    name,
    handle,
    avatar,
    profileUrl,
    isPending,
    contentStatus,
    tags,
    followersLabel,
    engagementLabel,
    platform = '—',
    onMarkComplete,
    markCompleteLoading = false,
    canMarkComplete = false,
    blockingDeliverables = [],
}: IParticipantProfileHeaderProps) {
    const initials = name
        .split(/\s+/)
        .map(w => w[0])
        .join('')
        .slice(0, 2);
    const statusColor = CONTENT_STATUS_COLORS[contentStatus || ''] || BRAND_LUXE_SECONDARY;

    return (
        <Box
            sx={{
                bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                borderRadius: 2,
                p: 3,
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 3,
                alignItems: { md: 'center' },
            }}>
            <Avatar
                component={profileUrl ? 'a' : 'div'}
                href={profileUrl}
                target={profileUrl ? '_blank' : undefined}
                rel={profileUrl ? 'noopener noreferrer' : undefined}
                src={avatar || undefined}
                sx={{
                    width: 80,
                    height: 80,
                    bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                    color: BRAND_LUXE_PRIMARY,
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: 28,
                    cursor: profileUrl ? 'pointer' : 'default',
                    textDecoration: 'none',
                    '&:hover': profileUrl ? { opacity: 0.85 } : undefined,
                }}>
                {initials}
            </Avatar>

            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
                    <Typography
                        sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 22, color: '#fff', letterSpacing: '-0.01em' }}>
                        {name}
                    </Typography>
                    {!isPending && contentStatus && (
                        <Chip
                            label={contentStatus}
                            size="small"
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 11,
                                height: 24,
                                bgcolor: `${statusColor}18`,
                                color: statusColor,
                            }}
                        />
                    )}
                    {isPending && (
                        <Chip
                            label="Pending Review"
                            size="small"
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 11,
                                height: 24,
                                bgcolor: `${BRAND_LUXE_PRIMARY}18`,
                                color: BRAND_LUXE_PRIMARY,
                            }}
                        />
                    )}
                </Box>
                <Typography
                    sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 14, color: 'rgba(255,255,255,0.45)', mb: 1 }}>
                    {handle}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                    <StatBlock label="Followers" value={followersLabel} />
                    <StatBlock label="Eng. Rate" value={engagementLabel} color={BRAND_LUXE_SECONDARY} />
                    <StatBlock label="Platform" value={platform} />
                    {tags.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {tags.map(t => (
                                <Chip
                                    key={t}
                                    label={t}
                                    size="small"
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 10,
                                        height: 22,
                                        bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                        color: '#fff',
                                    }}
                                />
                            ))}
                        </Box>
                    )}
                </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, flexShrink: 0, alignSelf: { xs: 'stretch', md: 'center' } }}>
                <Tooltip
                    title={
                        !canMarkComplete && blockingDeliverables.length > 0
                            ? `Waiting for live URLs: ${blockingDeliverables.join(', ')}`
                            : !canMarkComplete
                            ? 'All approved deliverables must have live URLs submitted'
                            : ''
                    }
                    disableHoverListener={canMarkComplete}
                    placement="top">
                    <span>
                        <Button
                            variant="contained"
                            startIcon={
                                markCompleteLoading ? (
                                    <CircularProgress size={16} sx={{ color: '#004734' }} />
                                ) : (
                                    <CheckCircleOutlineIcon />
                                )
                            }
                            disabled={!canMarkComplete || markCompleteLoading}
                            onClick={onMarkComplete}
                            sx={{
                                py: 1,
                                px: 3,
                                borderRadius: 999,
                                textTransform: 'none',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 13,
                                bgcolor: BRAND_LUXE_SECONDARY,
                                color: '#004734',
                                boxShadow: 'none',
                                '&:hover': { bgcolor: '#00edb4' },
                                '&.Mui-disabled': { bgcolor: `${BRAND_LUXE_SECONDARY}50`, color: '#004734' },
                            }}>
                            {markCompleteLoading ? 'Processing…' : 'Mark Complete'}
                        </Button>
                    </span>
                </Tooltip>
            </Box>
        </Box>
    );
}

function StatBlock({ label, value, color = '#fff' }: { label: string; value: string; color?: string }) {
    return (
        <Box>
            <Typography
                sx={{
                    fontSize: 10,
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    textTransform: 'uppercase',
                    letterSpacing: '0.12em',
                    color: 'rgba(255,255,255,0.4)',
                }}>
                {label}
            </Typography>
            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 15, color, textTransform: 'capitalize' }}>
                {value}
            </Typography>
        </Box>
    );
}
